import { error } from "console";
import { QuestionaryQuestion } from "../models/QuestionaryQuestion";
import { Questionary } from "../models/Questionary";
import { QuestionaryDetail } from "../models/QuestionaryDetail";
import { QuestionarySection } from "../models/QuestionarySection";
import axios, { AxiosRequestConfig } from "axios";
import { saveAs } from "file-saver";
import { AuditModule } from "../models/AuditModule";

const API_BASE_URL = process.env.REACT_APP_AUDIT_API_URL;

// ============================================
// TIPOS PARA PAGINACIÓN (NUEVOS)
// ============================================

export interface PagedResult<T> {
  data: T[];
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface QuestionaryQueryParams {
  isCompleted: boolean;
  pageNumber?: number;
  pageSize?: number;
  includeArchived?: boolean;
  searchText?: string;
}

// ============================================
// MÉTODOS EXISTENTES - SIN CAMBIOS
// ============================================

const getQuestionaryById = async (id: number): Promise<Questionary> => {
  const response = await fetch(`${API_BASE_URL}/Questionary/GetById?id=${id}`);
  const data = await response.json();
  return data;
};

const getSections = async (
  policyTypeId: number,
): Promise<QuestionarySection[]> => {
  const response = await fetch(
    `${API_BASE_URL}/Questionary/GetSections?policyTypeId=${policyTypeId}`,
  );
  if (!response.ok) {
    throw new Error("Failed to fetch sections");
  }

  const sections: QuestionarySection[] = await response.json();

  // Ordenar secciones por posición
  return sections.sort((a, b) => a.position - b.position);
};

const getQuestions = async (
  subsectionId: number,
  questionaryId: number,
): Promise<QuestionaryQuestion[]> => {
  const response = await fetch(
    `${API_BASE_URL}/Questionary/GetQuestions?subsectionId=${subsectionId}&QuestionaryId=${questionaryId}`,
  );
  const data = await response.json();
  return data;
};

const getQuestionById = async (
  questionId: number,
): Promise<QuestionaryQuestion> => {
  const response = await fetch(
    `${API_BASE_URL}/Questionary/GetQuestionById?questionId=${questionId}`,
  );
  const data = await response.json();
  return data;
};

const getQuestionaryDetail = async (
  questionaryId: number,
): Promise<QuestionaryDetail[]> => {
  const response = await fetch(
    `${API_BASE_URL}/QuestionaryDetail/GetByQuestionaryId?questionaryId=${questionaryId}`,
  );
  const data = await response.json();
  return data;
};

const saveQuestionaryDetail = async (
  questionaryDetails: QuestionaryDetail[],
) => {
  const response = await fetch(`${API_BASE_URL}/QuestionaryDetail/Save`, {
    method: "POST",
    headers: { "Content-Type": "application/json; charset= utf-8" },
    body: JSON.stringify(questionaryDetails),
  });

  return response;
};

const saveQuestionary = async (questionary: Questionary) => {
  const response = await fetch(`${API_BASE_URL}/Questionary/Save`, {
    method: "POST",
    headers: { "Content-Type": "application/json; charset= utf-8" },
    body: JSON.stringify(questionary),
  });

  return response;
};

const changeStatus = async (questionaryId: number, statusId: number) => {
  const response = await fetch(
    `${API_BASE_URL}/Questionary/ChangeStatus?questionaryId=${questionaryId}&statusId=${statusId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json; charset= utf-8" },
    },
  );

  return response;
};

const saveComments = async (questionaryDetails: QuestionaryDetail[]) => {
  const response = await fetch(
    `${API_BASE_URL}/QuestionaryDetail/SaveComments`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json; charset= utf-8" },
      body: JSON.stringify(questionaryDetails),
    },
  );

  return response;
};

const getQuestionaryDetailIsSaved = async (
  questionaryId: number,
): Promise<Boolean[]> => {
  const response = await fetch(
    `${API_BASE_URL}/QuestionaryDetail/IsSaved?questionaryId=${questionaryId}`,
  );
  const data = await response.json();
  return data;
};

const exportToExcel = async (questionaryId: number) => {
  let url = `${API_BASE_URL}/QuestionaryDetail/ExportToExcel?questionaryId=${questionaryId}`;
  let method = "GET";
  let instance = axios.create({ baseURL: url });
  let options: AxiosRequestConfig = {
    url,
    method,
    responseType: "blob",
  };
  return instance.request<any>(options).then((response) => {
    let url = window.URL.createObjectURL(new Blob([response.data]));
    saveAs(url, "Cuestionario.xlsx");
  });
};

const deleteQuestionary = async (questionaryId: number) => {
  const response = await fetch(
    `${API_BASE_URL}/Questionary/Delete?questionaryId=${questionaryId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json; charset= utf-8" },
    },
  );

  return response;
};

const getAllQuestionsForType = async (
  questionaryTypeId: number,
): Promise<QuestionaryQuestion[]> => {
  const response = await fetch(
    `${API_BASE_URL}/Questionary/GetAllQuestionsForType?questionaryTypeId=${questionaryTypeId}`,
  );
  if (!response.ok) {
    throw new Error("Failed to fetch all questions for type");
  }
  const data = await response.json();
  return data;
};

const getQuestionaryDetailById = async (
  questionaryDetailId: number,
): Promise<QuestionaryDetail> => {
  const response = await fetch(
    `${API_BASE_URL}/QuestionaryDetail/GetDetailById?questionaryDetailId=${questionaryDetailId}`,
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch questionary detail by ID: ${errorText}`);
  }
  const data = await response.json();
  return data;
};

const archived = async (questionary: Questionary) => {
  const response = await fetch(`${API_BASE_URL}/Questionary/Archive`, {
    method: "POST",
    headers: { "Content-Type": "application/json; charset= utf-8" },
    body: JSON.stringify(questionary),
  });

  return response;
};

// ============================================
// MÉTODOS OPTIMIZADOS CON PAGINACIÓN
// ============================================

/**
 * OPTIMIZADO: Obtiene todos los cuestionarios con soporte de paginación
 * Si no se pasan pageNumber/pageSize, se comporta como antes (trae todos)
 * Para usar paginación, pasar pageNumber y pageSize
 */
const getAllQuestionaries = async (
  isCompleted: boolean,
  pageNumber?: number,
  pageSize?: number,
  includeArchived?: boolean,
  searchText?: string,
): Promise<Questionary[] | PagedResult<Questionary>> => {
  // Construir query params
  const params = new URLSearchParams({
    isCompleted: isCompleted ? "true" : "false",
  });

  // Si se pasan parámetros de paginación, agregarlos
  if (pageNumber !== undefined)
    params.append("pageNumber", pageNumber.toString());
  if (pageSize !== undefined) params.append("pageSize", pageSize.toString());
  if (includeArchived !== undefined)
    params.append("includeArchived", includeArchived.toString());
  if (searchText) params.append("searchText", searchText);

  const response = await fetch(
    `${API_BASE_URL}/Questionary/GetAllQuestionaries?${params.toString()}`,
  );
  const data = await response.json();

  // Si la respuesta tiene estructura de paginación, retornarla como PagedResult
  // Si no, retornar el array directo (backward compatibility)
  if (data.data && data.totalRecords !== undefined) {
    return data as PagedResult<Questionary>;
  }

  return data as Questionary[];
};

/**
 * OPTIMIZADO: Obtiene cuestionarios por userId con soporte de paginación
 * Si no se pasan pageNumber/pageSize, se comporta como antes (trae todos)
 */
const getQuestionariesByUserId = async (
  userId: number,
  isCompleted: boolean,
  pageNumber?: number,
  pageSize?: number,
  includeArchived?: boolean,
  searchText?: string,
): Promise<Questionary[] | PagedResult<Questionary>> => {
  const params = new URLSearchParams({
    userId: userId.toString(),
    isCompleted: isCompleted ? "true" : "false",
  });

  if (pageNumber !== undefined)
    params.append("pageNumber", pageNumber.toString());
  if (pageSize !== undefined) params.append("pageSize", pageSize.toString());
  if (includeArchived !== undefined)
    params.append("includeArchived", includeArchived.toString());
  if (searchText) params.append("searchText", searchText);

  const response = await fetch(
    `${API_BASE_URL}/Questionary/GetQuestionariesByUserId?${params.toString()}`,
  );
  const data = await response.json();

  if (data.data && data.totalRecords !== undefined) {
    return data as PagedResult<Questionary>;
  }

  return data as Questionary[];
};

/**
 * OPTIMIZADO: Obtiene cuestionarios por email con soporte de paginación
 * Si no se pasan pageNumber/pageSize, se comporta como antes (trae todos)
 */
const getQuestionariesByEmail = async (
  email: string,
  isCompleted: boolean,
  pageNumber?: number,
  pageSize?: number,
  includeArchived?: boolean,
  searchText?: string,
): Promise<Questionary[] | PagedResult<Questionary>> => {
  const params = new URLSearchParams({
    email: email,
    isCompleted: isCompleted ? "true" : "false",
  });

  if (pageNumber !== undefined)
    params.append("pageNumber", pageNumber.toString());
  if (pageSize !== undefined) params.append("pageSize", pageSize.toString());
  if (includeArchived !== undefined)
    params.append("includeArchived", includeArchived.toString());
  if (searchText) params.append("searchText", searchText);

  const response = await fetch(
    `${API_BASE_URL}/Questionary/GetQuestionariesByEmail?${params.toString()}`,
  );
  const data = await response.json();

  if (data.data && data.totalRecords !== undefined) {
    return data as PagedResult<Questionary>;
  }

  return data as Questionary[];
};

/**
 * NUEVO: Obtiene solo cuestionarios archivados
 */
const getArchivedQuestionaries = async (
  userId?: number,
  email?: string,
  role?: string,
  pageNumber?: number,
  pageSize?: number,
  searchText?: string,
): Promise<Questionary[] | PagedResult<Questionary>> => {
  const params = new URLSearchParams();

  if (userId) params.append("userId", userId.toString());
  if (email) params.append("email", email);
  if (role) params.append("role", role);
  if (pageNumber !== undefined)
    params.append("pageNumber", pageNumber.toString());
  if (pageSize !== undefined) params.append("pageSize", pageSize.toString());
  if (searchText) params.append("searchText", searchText);

  const response = await fetch(
    `${API_BASE_URL}/Questionary/GetArchivedQuestionaries?${params.toString()}`,
  );

  if (!response.ok) {
    throw new Error("Error al cargar cuestionarios archivados");
  }

  const data = await response.json();

  // Si la respuesta tiene estructura de paginación, retornarla como PagedResult
  if (data.data && data.totalRecords !== undefined) {
    return data as PagedResult<Questionary>;
  }

  return data as Questionary[];
};

// ============================================
// EXPORTAR SERVICIO
// ============================================

const QuestionaryService = {
  // Métodos existentes (sin cambios)
  getQuestionaryById,
  getSections,
  getQuestions,
  getQuestionById,
  saveQuestionaryDetail,
  getQuestionaryDetail,
  getQuestionaryDetailIsSaved,
  saveComments,
  exportToExcel,
  saveQuestionary,
  changeStatus,
  deleteQuestionary,
  getAllQuestionsForType,
  getQuestionaryDetailById,
  archived,

  // Métodos optimizados (backward compatible)
  getAllQuestionaries,
  getQuestionariesByUserId,
  getQuestionariesByEmail,

  // Nuevo método
  getArchivedQuestionaries,
};

export default QuestionaryService;
